import {
  Box,
  Center,
  HStack,
  IconButton,
  Text,
  useToast,
} from '@chakra-ui/react';
import { usePaginationAdmin } from '@diamond/shared/context';
import { DeleteDataWrapper } from '@diamond/shared/data-access';
import {
  BackofficeActivitiesOrderItem,
  BackofficeOrderDetail,
} from '@diamond/shared/types';
import {
  Alert,
  DataTable,
  DISPLAY_IN_LIMITS,
  Dropdown,
  Pagination,
  showToast,
} from '@diamond/shared/ui';
import { LIST_FEATURE_FLAGS, useFeatureFlag } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { DeleteOutlined as DeleteOutlinedIcon } from '@mui/icons-material';
import { ColumnDef, Row } from '@tanstack/react-table';
import cleanDeep from 'clean-deep';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useActivitiesDetail } from '../../api';
import { buildActivityOrderItems } from '../../utils/build-activity-order-items';
import {
  createInfoColumns,
  quantityColumns,
  useEditableColumns,
} from '../activity-detail-columns';
import { useActivityDetailGroupedColumns } from './activity-detail-grouped-columns';
import { ActivityDetailGroupedTable } from './activity-detail-grouped-table';
import { ExpandTableToggle } from './expand-table-toggle';

type Props = {
  activitiesId: string;
  cancelReasonPerItem: { label: string; value: string }[];
  isDisable: boolean;
};

export type PaginationProps = {
  page: number;
  pageCount: number;
  nextPage: () => void;
  previousPage: () => void;
  firstPage: () => void;
  lastPage: () => void;
  size: number;
  setSize: (size: number) => void;
};

type TableSectionProps = {
  activitiesId: string;
  cancelReasonPerItem: { label: string; value: string }[];
  isDisable: boolean;
  items: BackofficeActivitiesOrderItem[] | undefined;
  isFetching: boolean;
  reload: boolean;
  page: number;
  queryString: {
    order_by: string;
    direction: string;
    size: number;
    page: number;
  };
  refetch: () => void;
  selectedData: Row<BackofficeActivitiesOrderItem>[];
  setSelectedData: (rows: Row<BackofficeActivitiesOrderItem>[]) => void;
  isVoucherMenuEnabled: boolean;
  accessToken: string;
  toast: ReturnType<typeof useToast>;
  orderStatus?: BackofficeOrderDetail['order']['status'];
};

function LegacyActivityDetailTableSection({
  activitiesId,
  cancelReasonPerItem,
  isDisable,
  items,
  isFetching,
  reload,
  page,
  queryString,
  refetch,
  selectedData,
  setSelectedData,
  isVoucherMenuEnabled,
  accessToken,
  toast,
}: TableSectionProps) {
  const editableColumns = useEditableColumns(
    setSelectedData,
    selectedData,
    () => void refetch(),
    activitiesId,
    cancelReasonPerItem,
    isDisable,
    [`Activities-${activitiesId}`, page, cleanDeep(queryString)],
    accessToken,
    toast,
    isVoucherMenuEnabled
  );

  const columns: ColumnDef<BackofficeActivitiesOrderItem, unknown>[] =
    useMemo(() => {
      const infoColumns = createInfoColumns();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tableColumns: ColumnDef<any, any>[] = [
        ...editableColumns.checkboxColumn,
        ...infoColumns,
        ...editableColumns.editableColumns,
        ...quantityColumns,
      ];
      return tableColumns as ColumnDef<
        BackofficeActivitiesOrderItem,
        unknown
      >[];
    }, [editableColumns]);

  return (
    <DataTable
      key={reload.toString()}
      columns={columns}
      data={
        (Array.isArray(items) ? items : []) as BackofficeActivitiesOrderItem[]
      }
      isLoading={isFetching}
    />
  );
}

function GroupedActivityDetailTableSection({
  activitiesId,
  cancelReasonPerItem,
  isDisable,
  items,
  isFetching,
  reload,
  page,
  queryString,
  refetch,
  selectedData,
  setSelectedData,
  isVoucherMenuEnabled,
  accessToken,
  toast,
  orderStatus,
}: TableSectionProps) {
  const [isTableExpanded, setIsTableExpanded] = useState(true);

  const columns = useActivityDetailGroupedColumns({
    setSelectedData,
    selectedData,
    refetch: () => void refetch(),
    activitiesId,
    cancelReasonData: cancelReasonPerItem,
    isDisable,
    queryKey: [`Activities-${activitiesId}`, page, cleanDeep(queryString)],
    token: accessToken,
    toast,
    isVoucherEnabled: isVoucherMenuEnabled,
    isExpanded: isTableExpanded,
    orderStatus,
  });

  return (
    <Box display="flex" flexDirection="column" alignItems="stretch">
      <ExpandTableToggle
        isExpanded={isTableExpanded}
        onToggle={() => setIsTableExpanded((prev) => !prev)}
      />
      <ActivityDetailGroupedTable
        key={`${reload.toString()}-${
          isTableExpanded ? 'expanded' : 'collapsed'
        }`}
        columns={columns}
        data={
          (Array.isArray(items) ? items : []) as BackofficeActivitiesOrderItem[]
        }
        isLoading={isFetching}
        isExpanded={isTableExpanded}
      />
    </Box>
  );
}

export function ActivityDetailItemsTable({
  activitiesId,
  cancelReasonPerItem,
  isDisable,
}: Props) {
  const toast = useToast();
  const session = useAuthStore();
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(15);
  const [reload, setReload] = useState(false);
  const [selectedData, setSelectedData] = useState<
    Row<BackofficeActivitiesOrderItem>[]
  >([]);
  const methods = useForm();

  const queryString = useMemo(
    () => ({ order_by: '', direction: '', size, page }),
    [size, page]
  );

  const isVoucherMenuEnabled = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_VOUCHERS_MENU,
    false
  );

  const isGroupedTableEnabled = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_ADMIN_ACTIVITY_DETAIL_GROUPED_TABLE,
    false
  );

  const selectPromoItems = useCallback(
    (d: BackofficeOrderDetail) =>
      buildActivityOrderItems(d, {
        injectPromoFreeGoods: !isVoucherMenuEnabled,
      }),
    [isVoucherMenuEnabled]
  );

  const selectActivityDetailTableData = useCallback(
    (d: BackofficeOrderDetail) => ({
      items: selectPromoItems(d),
      pageCount: d.pagination.pages,
      orderStatus: d.order.status,
    }),
    [selectPromoItems]
  );

  const {
    activitiesDetailData: tableData,
    isFetching,
    refetch,
  } = useActivitiesDetail(activitiesId, queryString, {
    select: selectActivityDetailTableData,
    prefetchNextPage: false,
  });

  const items = tableData?.items;
  const pageCount = tableData?.pageCount ?? 1;
  const orderStatus = tableData?.orderStatus;

  const { nextPage, previousPage, lastPage, firstPage } = usePaginationAdmin(
    page,
    setPage,
    refetch,
    pageCount
  );

  const { deleteDataWithCustomObject: deleteAdditionalProduct } =
    DeleteDataWrapper(
      `backoffice/order/${activitiesId}`,
      session.access_token,
      'admin'
    );

  const showDeleteItem =
    selectedData.length > 0 &&
    selectedData.every((e) => e.original.is_new_product === true);

  const handleDeleteAdditionalProduct = async () => {
    const product_ids = selectedData.map(
      (selected) => selected.original.product_id
    );
    deleteAdditionalProduct('item', { product_ids })
      .then(() => {
        void refetch();
        setSelectedData([]);
        setReload(!reload);
        showToast(toast, 'success', 'Produk berhasil dihapus!');
      })
      .catch((err: { message?: string }) => {
        showToast(toast, 'error', err?.message || 'Gagal menghapus produk');
      });
  };

  const isMBGWarningEnabled = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_MBG_WARNING,
    false
  );

  /*
   * MBG Warning Logic
   * Checks for ALL items flagged with 'requires_manual_process'
   * Displays dynamic warning message with multiple product names
   */
  const mbgItems = useMemo(() => {
    return items?.filter((item) => item.requires_manual_process) || [];
  }, [items]);

  const showMBGWarning = useMemo(() => {
    return isMBGWarningEnabled && mbgItems.length > 0;
  }, [isMBGWarningEnabled, mbgItems]);

  const tableSectionProps: TableSectionProps = {
    activitiesId,
    cancelReasonPerItem,
    isDisable,
    items: items as BackofficeActivitiesOrderItem[] | undefined,
    isFetching,
    reload,
    page,
    queryString,
    refetch: () => void refetch(),
    selectedData,
    setSelectedData,
    isVoucherMenuEnabled,
    accessToken: session.access_token,
    toast,
    orderStatus,
  };

  return (
    <Box my={8} width="full">
      {showMBGWarning && mbgItems.length > 0 && (
        <Box my={4}>
          <Alert
            status="error"
            colorScheme="red"
            description={`Jangan proses pesanan produk ${mbgItems
              .map((item) => item.title)
              .join(
                ', '
              )} lebih dari atau sama dengan 20 karton! (Khusus Jakarta)`}
            className="text-center"
            boxShadow="none"
          />
        </Box>
      )}
      {showDeleteItem ? (
        <IconButton
          aria-label="Delete"
          icon={<DeleteOutlinedIcon />}
          fontSize="xs"
          variant="solid"
          borderColor="gray.33"
          background="red"
          color="white"
          onClick={handleDeleteAdditionalProduct}
        />
      ) : null}
      {isGroupedTableEnabled ? (
        <GroupedActivityDetailTableSection {...tableSectionProps} />
      ) : (
        <LegacyActivityDetailTableSection {...tableSectionProps} />
      )}
      <Center my={8}>
        <HStack spacing={4}>
          <Pagination
            currentPage={page}
            totalPage={pageCount}
            nextPage={nextPage}
            firstPage={firstPage}
            lastPage={lastPage}
            previousPage={previousPage}
          />
          <HStack>
            <Text fontSize="sm">Lihat</Text>
            <Dropdown
              register={methods.register}
              name="page-size"
              placeholder=""
              items={DISPLAY_IN_LIMITS}
              defaultValue="15"
              onClick={() => setSize(methods.watch('page-size'))}
            />
            <Text fontSize="sm">Per halaman</Text>
          </HStack>
        </HStack>
      </Center>
    </Box>
  );
}

export default ActivityDetailItemsTable;
